type AuthFetch = (endpoint: string, init?: RequestInit) => Promise<Response>;

export const MAX_TAG_LENGTH = 60;

export interface TagPreviewResult {
  allowed: boolean;
  conflicts: Array<{ reason?: string } | string>;
  errors: string[];
}

export function validateLocalTagShape(rawTag: string): string[] {
  const tag = rawTag.trim();
  const errors: string[] = [];
  if (!tag) return ['Tag cannot be empty.'];
  if (tag.includes('\n') || tag.includes('\r')) {
    errors.push('Tag cannot contain line breaks.');
  }
  if (tag.length > MAX_TAG_LENGTH) {
    errors.push(`Tag cannot exceed ${MAX_TAG_LENGTH} characters.`);
  }
  return errors;
}

export async function previewTagAddition(
  authFetch: AuthFetch,
  entityType: 'job' | 'resume',
  existingTags: string[],
  newTag: string,
): Promise<TagPreviewResult> {
  const localErrors = validateLocalTagShape(newTag);
  if (localErrors.length > 0) {
    return { allowed: false, conflicts: [], errors: localErrors };
  }

  const res = await authFetch('/api/tag-intelligence/preview', {
    method: 'POST',
    body: JSON.stringify({ entityType, existingTags, newTag }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    return {
      allowed: false,
      conflicts: [],
      errors: extractTagErrors(data),
    };
  }

  return {
    allowed: Boolean(data?.allowed),
    conflicts: Array.isArray(data?.conflicts) ? data.conflicts : [],
    errors: Array.isArray(data?.errors) ? data.errors : [],
  };
}

export function extractTagErrors(payload: any): string[] {
  const detail = payload?.detail ?? payload;
  const errors = Array.isArray(detail?.errors) ? detail.errors : [];
  const conflicts = Array.isArray(detail?.conflicts) ? detail.conflicts : [];
  const message = typeof detail?.message === 'string' ? [detail.message] : [];
  const fallback = typeof detail === 'string' ? [detail] : [];
  return [...errors, ...conflicts, ...message, ...fallback].filter(Boolean);
}

export function formatConflictMessages(items: Array<{ reason?: string } | string>): string[] {
  return items.map(item => (typeof item === 'string' ? item : item.reason || 'Tag conflict detected.'));
}